/* Their pumps, managed from the plant side — add a pump company's individual units so
   dispatch can allocate one to a job without waiting on the pumper to log in. Each pump
   keeps its own permanent operator QR. */
import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Construction, Plus, Trash2, Copy, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { printQrPage } from "@/lib/printQr";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full rounded-none bg-transparent border border-industrial mono text-sm p-2";

export const PumpUnitsDialog = ({ company, onClose }) => {
  const [units, setUnits] = useState([]);
  const [f, setF] = useState({ name: "", operator_name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [qrId, setQrId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/pump-companies/${company.id}/pump-units`); setUnits(r.data.units || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load their pumps"); }
  }, [company.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.name.trim()) return toast.error("Give the pump a name, e.g. Boom 32m");
    setBusy(true);
    try {
      await api.post(`/pump-companies/${company.id}/pump-units`, f);
      setF({ name: "", operator_name: "", phone: "" });
      toast.success("Pump added");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add the pump"); }
    finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Remove ${u.name}? Its operator QR will stop working.`)) return;
    try { await api.delete(`/pump-companies/${company.id}/pump-units/${u.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  const urlFor = (u) => `${window.location.origin}/pump-unit/${u.track_token}`;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="pump-units-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase tracking-wide">{company.name} · Their Pumps</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-industrial -mt-2">
          Add each pump they run so you can allocate the right one on an order. Every pump gets its own
          permanent QR for that operator — they'll see only the jobs their pump is on.
        </p>

        <div className="space-y-2">
          {units.length === 0 && (
            <div className="border border-dashed border-industrial p-5 text-center mono text-[11px] uppercase tracking-widest text-muted-industrial" data-testid="pump-units-empty">
              No pumps listed yet
            </div>
          )}
          {units.map((u) => (
            <div key={u.id} className="border border-industrial p-3" data-testid={`pump-unit-row-${u.id}`}>
              <div className="flex items-center gap-2">
                <Construction className="w-4 h-4 text-primary-orange shrink-0" />
                <div className="min-w-0">
                  <div className="font-bold uppercase truncate">{u.name}</div>
                  <div className="mono text-[11px] text-muted-industrial truncate">{u.operator_name || "operator TBA"}{u.phone ? ` · ${u.phone}` : ""}</div>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  {u.track_token && (
                    <button type="button" data-testid={`pump-unit-qr-${u.id}`} onClick={() => setQrId(qrId === u.id ? null : u.id)}
                      className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black">QR</button>
                  )}
                  <button type="button" data-testid={`pump-unit-del-${u.id}`} onClick={() => del(u)}
                    className="text-muted-industrial hover:text-red-500 p-1.5"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {qrId === u.id && u.track_token && (
                <div className="mt-3 flex gap-3" data-testid={`pump-unit-qr-panel-${u.id}`}>
                  <div className="p-2 bg-white self-start"><QRCodeSVG id={`pu-qr-${u.id}`} value={urlFor(u)} size={110} level="M" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="mono text-[10px] break-all border border-industrial px-2 py-1">{urlFor(u)}</div>
                    <div className="flex gap-2 mt-2">
                      <button type="button" data-testid={`pump-unit-copy-${u.id}`} onClick={() => { navigator.clipboard?.writeText(urlFor(u)); toast.success("Copied"); }}
                        className="mono text-[10px] uppercase tracking-widest border border-industrial px-3 py-1.5 hover:border-primary-orange hover:text-primary-orange inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                      <button type="button" data-testid={`pump-unit-print-${u.id}`}
                        onClick={() => printQrPage({ title: `Pump Tracker · ${u.name}`, tagLine: "ARMORBATCH GLOBAL · PUMP TRACKER",
                          heading: `${company.name} · ${u.name}`,
                          description: "Scan with your phone camera to see every concrete truck allocated to this pump today — live GPS and ETAs.",
                          footer: "© ARMORBATCH GLOBAL · Live pump tracker", url: urlFor(u), svgElementId: `pu-qr-${u.id}` })}
                        className="mono text-[10px] uppercase tracking-widest border border-industrial px-3 py-1.5 hover:border-primary-orange hover:text-primary-orange inline-flex items-center gap-1"><Printer className="w-3 h-3" /> Print</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-industrial pt-3 space-y-2">
          <div className="label-tag text-primary-orange">Add a pump</div>
          <input data-testid="pump-unit-name" value={f.name} placeholder="Pump name — e.g. Boom 32m"
            onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="pump-unit-operator" value={f.operator_name} placeholder="Operator"
              onChange={(e) => setF({ ...f, operator_name: e.target.value })} className={inputCls} />
            <input data-testid="pump-unit-phone" value={f.phone} placeholder="Mobile"
              onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} />
          </div>
          <button type="button" onClick={add} disabled={busy} data-testid="pump-unit-save"
            className="w-full bg-primary-orange text-black font-black uppercase tracking-widest text-xs py-2.5 disabled:opacity-50 inline-flex items-center justify-center gap-1">
            <Plus className="w-4 h-4" /> {busy ? "Saving…" : "Add pump"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
